import { useMemo } from 'react';
import { DefaultTheme, useTheme } from 'styled-components';
import { ComponentsProps } from '../types';

/** use in class components with withTheme HOC */
export const getComponentProps = <T extends keyof ComponentsProps, K extends ComponentsProps[T]>(
    theme: DefaultTheme,
    component: T,
    customValue?: K | Partial<K> | ((defaultValue: K) => K)
): K => {
    const defaultValue = theme.settings.componentsProps[component] as K;

    if (!customValue) {
        return defaultValue;
    }
    if (typeof customValue === 'string') {
        return customValue ?? defaultValue;
    }
    if (typeof defaultValue === 'object' && typeof customValue === 'object') {
        const definedCustomValues = Object.fromEntries(
            Object.entries(customValue).filter(([, value]) => value != null)
        ) as Partial<K>;
        return { ...defaultValue, ...definedCustomValues } as K;
    }
    if (customValue instanceof Function) {
        return customValue(defaultValue);
    }
    return defaultValue;
};

/** use in functional components */
export const useComponentProps = <T extends keyof ComponentsProps, K extends ComponentsProps[T]>(
    component: T,
    customValue?: K | Partial<K> | ((defaultValue: K) => K)
): K => {
    const theme = useTheme();
    return useMemo(() => getComponentProps(theme, component, customValue), [theme, component, customValue]);
};
